import { ProductOffering } from '../interfaces';

export type ProductOfferingOrderType =
  | 'featured'
  | 'local'
  | 'regional'
  | 'global';

const PLANS_GLOBAL_ALIAS = 'GLOBAL';

export const resolveProductOfferingType = (
  plan: ProductOffering,
): ProductOfferingOrderType => {
  const { countries, featured } = plan;
  if (featured) {
    return 'featured';
  }

  const isGlobal = countries.some(
    (country) => country.alias === PLANS_GLOBAL_ALIAS,
  );
  if (isGlobal) {
    return 'global';
  }

  /*
  We have to exclude "countries" that are not countries (GLOBAL, EUROPE, CARIBBEAN etc.) - they are considered regional.
  Currently, we leverage on the fact that they all have more than 2 characters opposite to the ISO country codes.
   */
  const isLocal = countries.length === 1 && countries[0].alias.length === 2;
  if (isLocal) {
    return 'local';
  }

  return 'regional';
};

export const sortProductOfferingsBy = (
  sortType: string,
  plans: ProductOffering[],
) => {
  const plansByType: Record<ProductOfferingOrderType, ProductOffering[]> = {
    featured: [],
    global: [],
    regional: [],
    local: [],
  };

  const sortedPlans = [...plans].sort((a, b) => {
    const displayOrderA = a.display_order ?? Infinity;
    const displayOrderB = b.display_order ?? Infinity;
    if (displayOrderA !== displayOrderB) {
      return displayOrderA - displayOrderB;
    }
    switch (sortType) {
      case 'priceLowHigh':
        return sortProductOfferingsByLowPrice(a, b);
      case 'priceHighLow':
        return sortProductOfferingsByHighPrice(a, b);
      case 'dataLowHigh':
        return sortProductOfferingsByLowData(a, b);
      case 'dataHighLow':
        return sortProductOfferingsByHighData(a, b);
      default:
        return sortProductOfferingsByHighPrice(a, b);
    }
  });

  if (sortType === 'default') {
    sortedPlans.forEach((plan) =>
      plansByType[resolveProductOfferingType(plan)].push(plan),
    );
    return plansByType;
  }

  return { featured: [], global: [], regional: [], local: sortedPlans };
};

/**
 * Returns object of product offerings sorted by type.
 * It preserves original ordering
 */

export const sortProductOfferingsByHighPrice = (
  a: ProductOffering,
  b: ProductOffering,
) => {
  const aSellingPrice =
    a?.product_offering_pricing?.[0]?.total_price_gross ?? 0;
  const bSellingPrice =
    b?.product_offering_pricing?.[0]?.total_price_gross ?? 0;
  return bSellingPrice - aSellingPrice;
};

export const sortProductOfferingsByLowPrice = (
  a: ProductOffering,
  b: ProductOffering,
) => {
  const aSellingPrice =
    a?.product_offering_pricing?.[0]?.total_price_gross ?? 0;
  const bSellingPrice =
    b?.product_offering_pricing?.[0]?.total_price_gross ?? 0;
  return aSellingPrice - bSellingPrice;
};

export const sortProductOfferingsByHighData = (
  a: ProductOffering,
  b: ProductOffering,
) => {
  const aData = a?.data ?? 0;
  const bData = b?.data ?? 0;

  if (aData === -1 && bData !== -1) return -1;
  if (bData === -1 && aData !== -1) return 1;
  return bData - aData;
};

export const sortProductOfferingsByLowData = (
  a: ProductOffering,
  b: ProductOffering,
) => {
  const aData = a?.data ?? 0;
  const bData = b?.data ?? 0;

  if (aData === -1 && bData !== -1) return 1;
  if (bData === -1 && aData !== -1) return -1;
  return aData - bData;
};
